import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Alert,
  Spinner,
} from 'react-bootstrap';
import {
  FaDoorOpen,
  FaPlus,
  FaEdit,
  FaTrash,
  FaMapMarkerAlt,
  FaSearch,
} from 'react-icons/fa';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';
import colors from '../constants/colors';

const getAuthToken = () => localStorage.getItem('authToken');

const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    seat_units: '',
    seats_per_unit: '',
    type: 'classroom',
  });

  const roomTypes = [
    { value: 'classroom', label: 'Classroom' },
    { value: 'lab', label: 'Laboratory' },
    { value: 'hall', label: 'Hall' },
    { value: 'library', label: 'Library' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_rooms`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setRooms(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError(err.response?.data?.message || 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const token = getAuthToken();
      const payload = {
        name: formData.name,
        location: formData.location,
        seat_units: parseInt(formData.seat_units),
        seats_per_unit: parseInt(formData.seats_per_unit),
        type: formData.type,
      };

      const endpoint = editingRoom ? 'update_room' : 'create_room';
      if (editingRoom) {
        payload.id = editingRoom.id;
        payload.status = editingRoom.status || 'active';
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}${endpoint}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setSuccess(
          response.data.message ||
            `Room ${editingRoom ? 'updated' : 'created'} successfully`,
        );
        setShowModal(false);
        setFormData({
          name: '',
          location: '',
          seat_units: '',
          seats_per_unit: '',
          type: 'classroom',
        });
        setEditingRoom(null);
        fetchRooms();
      }
    } catch (err) {
      console.error(
        `Error ${editingRoom ? 'updating' : 'creating'} room:`,
        err,
      );
      setError(
        err.response?.data?.message ||
          `Failed to ${editingRoom ? 'update' : 'create'} room`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (room) => {
    setError('');
    setEditingRoom(room);
    setShowModal(true);

    // Load full room details from API
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_room?id=${room.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success' && response.data.data) {
        const roomData = response.data.data;
        setFormData({
          name: roomData.name,
          location: roomData.location,
          seat_units: roomData.seat_units.toString(),
          seats_per_unit: roomData.seats_per_unit.toString(),
          type: roomData.type,
        });
      }
    } catch (err) {
      console.error('Error loading room details:', err);
      // Fallback to existing data if API fails
      setFormData({
        name: room.name,
        location: room.location,
        seat_units: room.seat_units.toString(),
        seats_per_unit: room.seats_per_unit.toString(),
        type: room.type,
      });
    }
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Are you sure you want to delete "${room.name}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}delete_room?id=${room.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setSuccess(response.data.message || 'Room deleted successfully');
        fetchRooms();
      }
    } catch (err) {
      console.error('Error deleting room:', err);
      setError(err.response?.data?.message || 'Failed to delete room');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRoom(null);
    setFormData({
      name: '',
      location: '',
      seat_units: '',
      seats_per_unit: '',
      type: 'classroom',
    });
  };

  const getTotalSeats = (room) => {
    return room.seat_units * room.seats_per_unit;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'classroom':
        return 'primary';
      case 'lab':
        return 'info';
      case 'hall':
        return 'success';
      case 'library':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'danger';
  };

  // Filter rooms based on search term
  const filteredRooms = rooms.filter(
    (room) =>
      room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      room.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      room.type.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                marginRight: 16,
              }}
            >
              <FaDoorOpen />
              Rooms
            </span>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.sidebar.text,
                  fontSize: '0.9rem',
                }}
              />
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search rooms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '35px',
                  height: '30px',
                  backgroundColor: colors.card.background,
                  borderColor: colors.card.border,
                  color: colors.text.primary,
                }}
              />
            </div>
          </div>

          {/* Add Room Button */}
          <Button
            onClick={() => setShowModal(true)}
            variant="light"
            size="sm"
            disabled={submitting}
            style={{
              width: '32px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Add Room"
          >
            <FaPlus />
          </Button>
        </div>

        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            onClose={() => setSuccess('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {success}
          </Alert>
        )}

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Spinner
              animation="border"
              style={{ color: '#667eea', width: '50px', height: '50px' }}
            />
            <p style={{ marginTop: '20px', color: '#6c757d' }}>
              Loading rooms...
            </p>
          </div>
        ) : filteredRooms.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <FaDoorOpen
              style={{
                fontSize: '4rem',
                color: '#dee2e6',
                marginBottom: '20px',
              }}
            />
            <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
              No Rooms Found
            </h4>
            <p style={{ color: '#adb5bd' }}>
              {searchTerm
                ? 'No rooms match your search criteria.'
                : 'Get started by adding your first room.'}
            </p>
            {!searchTerm && (
              <Button
                onClick={() => setShowModal(true)}
                style={{
                  marginTop: '20px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                <FaPlus className="me-2" /> Add First Room
              </Button>
            )}
          </div>
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <Table hover style={{ marginBottom: 0 }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'white',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '60px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      Room Name
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      <FaMapMarkerAlt className="me-1" />
                      Location
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                      }}
                    >
                      Seat Units
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                      }}
                    >
                      Seats/Unit
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                      }}
                    >
                      Total Seats
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        width: '120px',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRooms.map((room, index) => (
                    <tr
                      key={room.id}
                      style={{
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#2c3e50',
                          fontWeight: '500',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {room.name}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {room.location}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Badge bg={getTypeColor(room.type)}>
                          {room.type.charAt(0).toUpperCase() +
                            room.type.slice(1)}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          color: '#2c3e50',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {room.seat_units}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          color: '#2c3e50',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {room.seats_per_unit}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          color: '#2c3e50',
                          fontWeight: '600',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {getTotalSeats(room)}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <Badge bg={getStatusColor(room.status)}>
                          {room.status.charAt(0).toUpperCase() +
                            room.status.slice(1)}
                        </Badge>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(room)}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '0.85rem',
                            }}
                            title="Edit Room"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(room)}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '0.85rem',
                            }}
                            title="Delete Room"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Summary Footer */}
            <div
              style={{
                padding: '16px 20px',
                background: '#f8f9fa',
                borderTop: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
              }}
            >
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Showing {filteredRooms.length} rooms
                {searchTerm && ' (filtered)'}
              </span>
            </div>
          </div>
        )}

        {/* Add/Edit Room Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderBottom: 'none',
            }}
          >
            <Modal.Title style={{ fontWeight: '700' }}>
              {editingRoom ? (
                <>
                  <FaEdit className="me-2" />
                  Edit Room
                </>
              ) : (
                <>
                  <FaPlus className="me-2" />
                  Add New Room
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body style={{ padding: '30px' }}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Room Name <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Room A1, Computer Lab"
                  required
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Location <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., First Floor - Block A"
                  required
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Room Type <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {roomTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Seat Units <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="seat_units"
                      value={formData.seat_units}
                      onChange={handleInputChange}
                      placeholder="e.g., 10"
                      min="1"
                      required
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                    <Form.Text className="text-muted">
                      Number of bench rows/units
                    </Form.Text>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Seats per Unit <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="seats_per_unit"
                      value={formData.seats_per_unit}
                      onChange={handleInputChange}
                      placeholder="e.g., 3"
                      min="1"
                      required
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                    <Form.Text className="text-muted">
                      Seats in each unit
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>

              {formData.seat_units && formData.seats_per_unit && (
                <Alert
                  variant="info"
                  style={{
                    marginBottom: 0,
                    borderRadius: '8px',
                    border: '1px solid rgba(13, 110, 253, 0.2)',
                  }}
                >
                  <strong>Total Seats:</strong>{' '}
                  {parseInt(formData.seat_units) *
                    parseInt(formData.seats_per_unit)}
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer
              style={{ borderTop: '1px solid #dee2e6', padding: '20px 30px' }}
            >
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                disabled={submitting}
                style={{
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600',
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '600',
                }}
              >
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    {editingRoom ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {editingRoom ? (
                      <>
                        <FaEdit className="me-2" />
                        Update Room
                      </>
                    ) : (
                      <>
                        <FaPlus className="me-2" />
                        Create Room
                      </>
                    )}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default Rooms;
